import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, ArrowRight, CheckCircle2, FileText } from "lucide-react";
import { GFFrame, GFHeader } from "@/components/GFFrame";
import { TEMPOH, GENDERS, AGE_RANGES, BUSINESS_MODELS, EMPLOYEE_COUNTS, REVENUE_RANGES } from "@/data/gfContent";
import { bgGetAssess, bgSubmitAssess } from "@/lib/gfApi";

const inputCls =
  "w-full rounded-2xl border border-[#E7E2D6] bg-white px-4 py-3 text-[15px] text-[#0F172A] outline-none transition-colors focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20";

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <label className="text-sm font-semibold text-slate-600">{label}</label>
    {children}
  </div>
);

const Sel = ({ value, onChange, options, placeholder, testId }) => (
  <select data-testid={testId} className={inputCls} value={value} onChange={onChange}>
    <option value="">{placeholder}</option>
    {options.map((o) => <option key={o} value={o}>{o}</option>)}
  </select>
);

export default function BGAssessment() {
  const { pid } = useParams();
  const [sp] = useSearchParams();
  const token = sp.get("t") || "";
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [profile, setProfile] = useState({
    tempoh_berniaga: "", business_model: "", employees: "", revenue_range: "", gender: "", age_range: "",
    revenue_current: "", avg_transaction: "", main_product: "", improve_90d: "",
  });
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    bgGetAssess(pid, token).then(setData).catch((e) => setErr(e?.response?.data?.detail || "Pautan tidak sah."));
  }, [pid, token]);

  const setP = (k) => (e) => setProfile({ ...profile, [k]: e.target.value });
  const answered = useMemo(() => Object.keys(answers).length, [answers]);

  const submit = async () => {
    const req = ["tempoh_berniaga", "business_model", "employees", "revenue_range", "gender", "age_range"];
    if (req.some((k) => !profile[k])) { toast.error("Sila lengkapkan maklumat perniagaan."); return; }
    if (!data || answered < data.questions.length) { toast.error("Sila jawab semua soalan."); return; }
    setSubmitting(true);
    try {
      const ordered = data.questions.map((q) => answers[q.id]);
      const r = await bgSubmitAssess(pid, { profile, answers: ordered }, token);
      toast.success("Penilaian selesai. Menjana laporan…");
      navigate(`/report/${r.assessment_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menghantar.");
    } finally { setSubmitting(false); }
  };

  if (err) return (
    <GFFrame testId="bg-assess-error">
      <div className="flex min-h-screen items-center justify-center px-8 text-center">
        <p className="text-slate-600" data-testid="bg-assess-error-msg">{err}</p>
      </div>
    </GFFrame>
  );
  if (!data) return (
    <GFFrame><div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#D4AF37]" /></div></GFFrame>
  );

  if (data.done && data.assessment_id) {
    return (
      <GFFrame testId="bg-assess-done">
        <div className="flex min-h-screen flex-col">
          <GFHeader />
          <div className="flex flex-1 flex-col items-center justify-center px-8 text-center">
            <CheckCircle2 className="h-12 w-12 text-[#2FA89F]" />
            <h2 className="mt-4 font-heading text-2xl font-bold text-[#0F172A]">Diagnostik anda telah lengkap</h2>
            <p className="mt-2 text-sm text-slate-500">Laporan Business Growth peribadi anda sedia untuk dilihat.</p>
            <button data-testid="bg-view-report-btn" onClick={() => navigate(`/report/${data.assessment_id}`)}
              className="mt-6 flex items-center gap-2 rounded-full bg-[#0F172A] px-6 py-3.5 text-sm font-bold text-white">
              <FileText className="h-4 w-4" /> Lihat Laporan Saya
            </button>
          </div>
        </div>
      </GFFrame>
    );
  }

  return (
    <GFFrame testId="bg-assess-page">
      <div className="flex min-h-screen flex-col">
        <GFHeader />
        <div className="flex flex-1 flex-col px-6 pb-32 pt-4">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="gf-gold-line mb-4" />
            <p className="text-xs font-bold uppercase tracking-widest text-[#B8912F]">Diagnostik Business Growth</p>
            <h2 className="mt-1 font-heading text-3xl font-bold leading-tight text-[#0F172A]">{data.participant.name}</h2>
            <p className="mt-1 text-sm text-slate-500">{data.participant.business_name || "—"}{data.participant.industri ? ` · ${data.participant.industri}` : ""}</p>
          </motion.div>

          <div className="mt-8">
            <h3 className="font-heading text-lg font-bold text-[#0F172A]">Maklumat Perniagaan</h3>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <Field label="Tempoh Berniaga"><Sel testId="bg-tempoh" value={profile.tempoh_berniaga} onChange={setP("tempoh_berniaga")} options={TEMPOH} placeholder="Pilih tempoh" /></Field>
              <Field label="Model Bisnes"><Sel testId="bg-model" value={profile.business_model} onChange={setP("business_model")} options={BUSINESS_MODELS} placeholder="Pilih model" /></Field>
              <Field label="Saiz Pasukan"><Sel testId="bg-employees" value={profile.employees} onChange={setP("employees")} options={EMPLOYEE_COUNTS} placeholder="Pilih saiz" /></Field>
              <Field label="Anggaran Hasil Bulanan"><Sel testId="bg-revenue" value={profile.revenue_range} onChange={setP("revenue_range")} options={REVENUE_RANGES} placeholder="Pilih julat" /></Field>
              <Field label="Jantina"><Sel testId="bg-gender" value={profile.gender} onChange={setP("gender")} options={GENDERS} placeholder="Pilih" /></Field>
              <Field label="Julat Umur"><Sel testId="bg-age" value={profile.age_range} onChange={setP("age_range")} options={AGE_RANGES} placeholder="Pilih" /></Field>
            </div>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <Field label="Hasil Bulanan Semasa (pilihan)"><input data-testid="bg-rev-current" className={inputCls} placeholder="cth: RM38,000" value={profile.revenue_current} onChange={setP("revenue_current")} /></Field>
              <Field label="Purata Nilai Transaksi (pilihan)"><input className={inputCls} placeholder="cth: RM50" value={profile.avg_transaction} onChange={setP("avg_transaction")} /></Field>
              <Field label="Produk/Servis Utama (pilihan)"><input className={inputCls} placeholder="cth: Set nasi lemak premium" value={profile.main_product} onChange={setP("main_product")} /></Field>
              <Field label="Ingin Diperbaiki 90 Hari (pilihan)"><input className={inputCls} placeholder="Tulis ringkas" value={profile.improve_90d} onChange={setP("improve_90d")} /></Field>
            </div>
          </div>

          <div className="mt-10">
            <h3 className="font-heading text-lg font-bold text-[#0F172A]">Penilaian ({answered}/{data.questions.length})</h3>
            <p className="mt-1 text-sm text-slate-500">Nilai setiap kenyataan dengan jujur mengikut skala.</p>
            <div className="mt-5 space-y-4">
              {data.questions.map((q, i) => (
                <div key={q.id} className="rounded-2xl border border-[#E7E2D6] bg-white p-4" data-testid={`bg-q-${q.id}`}>
                  <p className="text-[15px] font-medium text-[#0F172A]">{i + 1}. {q.text}</p>
                  <div className="mt-3 flex gap-2">
                    {data.scale.map((s) => (
                      <button key={s.value} data-testid={`bg-q-${q.id}-opt-${s.value}`} onClick={() => setAnswers({ ...answers, [q.id]: s.value })}
                        title={s.label}
                        className="h-10 w-10 rounded-full text-sm font-bold transition-colors"
                        style={answers[q.id] === s.value
                          ? { background: "#0F172A", color: "#fff" }
                          : { background: "#F5F1E6", color: "#0F172A" }}>
                        {s.value}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="fixed inset-x-0 bottom-0 mx-auto max-w-[520px] bg-gradient-to-t from-[#FBF8F0] via-[#FBF8F0] to-transparent px-6 py-4">
          <button data-testid="bg-submit-btn" onClick={submit} disabled={submitting}
            className="flex w-full items-center justify-center gap-3 rounded-full bg-[#0F172A] py-4 text-base font-bold text-white disabled:opacity-60">
            {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <ArrowRight className="h-5 w-5" />}
            {submitting ? "Menjana Laporan…" : `Hantar & Jana Laporan (${answered}/${data.questions.length})`}
          </button>
        </div>
      </div>
    </GFFrame>
  );
}
